import { IPartialDepositDetail } from 'app/core/interfaces/deposit.interface';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { Subscription } from 'rxjs';
import { ConciliationService } from 'app/core/services/conciliation.service';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { DepositAction } from 'app/core/enums/tabs-deposits.enum';

@Component({
  selector: 'app-deposits-details-copy-paste',
  templateUrl: './deposits-details-copy-paste.component.html',
  styleUrls: ['./deposits-details-copy-paste.component.scss'],
})
export class DepositsDetailsCopyPasteComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  formArray: FormArray;
  depositId: number;
  totalOfAmount = 0;
  totalOfAdjusment = 0;

  constructor(
    private fb: FormBuilder,
    private sweetAlertHelper: SweetAlertHelper,
    private t2LoadingService: T2LoadingService,
    protected objServ: ConciliationService
  ) {
    this.formArray = this.fb.array([]);
  }

  ngOnInit() {
    this.subscriptions.push(
      this.objServ.behaviorSubjDepositObs$.subscribe({
        next: ({ data }) => (this.depositId = data.depositId),
      })
    );
  }

  createFormGroupForDepositDetail(data: IPartialDepositDetail) {
    const { checkdate, checknbr, amount, adjustment } = data;

    return this.fb.group({
      depositid: [this.depositId],
      checkdate: [checkdate, Validators.required],
      checknbr: [checknbr, Validators.required],
      amount: [amount, Validators.required],
      adjustment: [adjustment, Validators.required],
      payor: 'Not Found',
    });
  }

  private transformTextToData(
    dataByClipboard: string
  ): IPartialDepositDetail[] | null {
    const formatDate = (date: string) => {
      const delimiter = date.includes('/') ? '/' : '-';
      const parts = date.split(delimiter);

      if (parts.length !== 3) {
        return '';
      }

      let [month, day, year] = parts;

      month = month.length === 1 ? '0' + month : month;
      day = day.length === 1 ? '0' + day : day;

      return [year, month, day].join('-');
    };

    const parseAmount = (value: string) => {
      value = value.trim();
      const isNegative = /^\(\$(.+)\)$/.test(value);

      if (isNegative) {
        return -parseFloat(value.replace(/[()$,]/g, ''));
      } else {
        return parseFloat(value.replace(/[$,]/g, ''));
      }
    };

    try {
      const lines = dataByClipboard.split('\n').filter((line) => line.length);
      return lines
        .map((line: string): IPartialDepositDetail => {
          const [checkdate, checknbr, amount, adjustment] = line
            .split('\t')
            .map((item) => item.replace(/\s+/g, ''));

          if (!checkdate || !checknbr || !amount) {
            return null;
          }

          const formattedDate = formatDate(checkdate);
          if (isNaN(new Date(formattedDate).getTime())) {
            return null;
          }

          const amountNumber = parseAmount(amount);

          let adjustmentNumber = 0;
          if (adjustment) {
            adjustmentNumber = parseAmount(adjustment);
          }

          if (isNaN(amountNumber) || isNaN(adjustmentNumber)) {
            return null;
          }

          return {
            depositid: 0,
            checkdate: formattedDate,
            checknbr: checknbr.replace(' ', ''),
            amount: amountNumber,
            adjustment: adjustmentNumber,
          };
        })
        .filter((f) => !!f);
    } catch (error) {
      return null;
    }
  }


  onPaste(event: ClipboardEvent) {
    const pastedText = event.clipboardData.getData('text');
    const data = this.transformTextToData(pastedText);

    if (!data) {
      this.showFormatAlert();
    }

    data.map((data: IPartialDepositDetail): void =>
      this.formArray.push(this.createFormGroupForDepositDetail(data))
    );

    this.updateDepositDetailsTotalInfo();

    this.formArray.controls.map((control) =>
      this.subscriptions.push(
        control.valueChanges.subscribe({
          next: () => this.updateDepositDetailsTotalInfo(),
        })
      )
    );
  }

  onSubmit(): void {
    this.t2LoadingService.show();
    this.objServ
      .createMassiveDepositDetails(this.formArray.value)
      .finally(() => this.t2LoadingService.hiden())
      .subscribe({
        next: (resp) => {
          if (!!resp.length) {
            this.closeAndRefreshDepositDetailGrid();
          }
        },
        error: (err) => this.sweetAlertHelper.captureException(err),
      });
  }

  closeAndRefreshDepositDetailGrid() {
    this.objServ.behaviorSubjDeposit.next({
      action: DepositAction.UpdateGridAfterCloseCopyPasteModal,
      data: null,
    });
  }

  resetInputs(): void {
    this.formArray = this.fb.array([]);
  }

  private showFormatAlert() {
    this.sweetAlertHelper
      .createCustomAlert({
        title: 'Attention',
        type: 'error',
        text: 'Format Invalid',
      })
      .then();
  }

  private updateDepositDetailsTotalInfo() {
    this.getTotalOfAmount();
    this.getTotalOfAdjusment();
  }

  private getTotalOfAmount(): void {
    const result = this.formArray.controls.reduce(
      (acc, el) => acc + Number(el.get('amount').value),
      0
    );

    this.totalOfAmount = result ? result : 0;
  }

  private getTotalOfAdjusment(): void {
    const result = this.totalOfAdjusment = this.formArray.controls.reduce(
      (acc, el) => acc + Number(el.get('adjustment').value),
      0
    );

    this.totalOfAdjusment =  result ? result : 0;
  }

  get disableSaveBtn(): boolean {
    return this.formArray.invalid;
  }

  get showForm(): boolean {
    return !!this.formArray.length;
  }

  get TotalOfDetails(): number {
    return this.formArray.length;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(
      (subscription: Subscription) => subscription.unsubscribe
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/billing-general/deposits/deposits-details-copy-paste/deposits-details-copy-paste.component.ts